<template>
  <main v-if="post" class="blog-detail-page">
    <section class="blog-hero">
      <div class="hero-text">
        <span class="category">{{ post.category }}</span>
        <h1>{{ post.title }}</h1>
        <p class="intro">{{ post.excerpt }}</p>
        <p class="date">{{ formattedDate }}</p>
      </div>

      <img class="hero-img" :src="post.cover" :alt="post.title" />
    </section>

    <section class="blog-layout" ref="blogLayoutRef">
      <article class="article">
        <div
          v-for="(block, index) in blocks"
          :key="index"
          class="content-block"
        >
          <h2 v-if="block.type === 'heading'">
            {{ block.text }}
          </h2>

          <h5 v-else-if="block.type === 'subheading'">
            {{ block.text }}
          </h5>

          <p v-else-if="block.type === 'paragraph'">
            {{ block.text }}
          </p>

          <figure v-else-if="block.type === 'image'" class="article-image">
            <img :src="block.image" :alt="block.alt" />
          </figure>

          <blockquote v-else-if="block.type === 'quote'">
            {{ block.text }}
          </blockquote>

          <div v-else-if="block.type === 'table'" class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th v-for="head in block.headers" :key="head">
                    {{ head }}
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="(row, rowIndex) in block.rows" :key="rowIndex">
                  <td v-for="(cell, cellIndex) in row" :key="cellIndex">
                    {{ cell }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <section v-else-if="block.type === 'cta'" class="article-cta">
            <h5>{{ block.title }}</h5>
            <a :href="block.link">
              {{ block.buttonText }}
            </a>
          </section>
        </div>

        <RouterLink to="/Blog" class="back-link"> ← 回到教育專欄 </RouterLink>
      </article>

      <aside class="side-wrap">
        <div
          ref="sideCardRef"
          class="side-card"
          :style="{ transform: `translateY(${sideCardY}px)` }"
        >
          <h3>{{ sideCta?.title || "索取相關教材資訊" }}</h3>

          <p>
            {{ sideCta?.subtitle || post.relatedTitle || post.title }}
          </p>

          <span>
            {{
              sideCta?.text ||
              post.relatedText ||
              "想了解更多教材資訊，歡迎與我們聯繫。"
            }}
          </span>

          <a :href="sideCta?.link || '/'" class="cta-btn">
            {{ sideCta?.buttonText || "Discover Phonics" }}
          </a>
        </div>
      </aside>
    </section>
  </main>

  <main v-else class="blog-detail-page">
    <p>文章讀取中...</p>
  </main>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useRoute } from "vue-router";
import { getBlogPosts } from "@/api/blogApi";

const route = useRoute();
const post = ref(null);

const blogLayoutRef = ref(null);
const sideCardRef = ref(null);
const sideCardY = ref(0);

const TOP_OFFSET = 120;

const updateSideCardPosition = () => {
  if (!blogLayoutRef.value || !sideCardRef.value) return;

  if (window.innerWidth <= 900) {
    sideCardY.value = 0;
    return;
  }

  const layoutRect = blogLayoutRef.value.getBoundingClientRect();
  const layoutTop = window.scrollY + layoutRect.top;
  const layoutHeight = blogLayoutRef.value.offsetHeight;
  const cardHeight = sideCardRef.value.offsetHeight;

  const currentY = window.scrollY + TOP_OFFSET - layoutTop;
  const maxY = layoutHeight - cardHeight;

  sideCardY.value = Math.min(Math.max(currentY, 0), Math.max(maxY, 0));
};

onMounted(async () => {
  const data = await getBlogPosts();
  post.value = data.find((item) => item.id === route.params.id);

  await nextTick();

  setTimeout(() => {
    updateSideCardPosition();
  }, 300);

  window.addEventListener("scroll", updateSideCardPosition);
  window.addEventListener("resize", updateSideCardPosition);
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", updateSideCardPosition);
  window.removeEventListener("resize", updateSideCardPosition);
});

const sideCta = computed(() => {
  if (!post.value?.content) return null;

  const lines = post.value.content
    .replace(/\\n/g, "\n")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const index = lines.findIndex((line) => line === "[SIDECTA]");
  if (index === -1) return null;

  return {
    title: lines[index + 1],
    subtitle: lines[index + 2],
    text: lines[index + 3],
    buttonText: lines[index + 4],
    link: lines[index + 5],
  };
});

const blocks = computed(() => {
  if (!post.value?.content) return [];

  const lines = post.value.content
    .replace(/\\n/g, "\n")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const result = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.startsWith("# ")) {
      result.push({ type: "heading", text: line.replace("# ", "") });
    } else if (line.startsWith("## ")) {
      result.push({ type: "subheading", text: line.replace("## ", "") });
    } else if (line === "[IMAGE]") {
      result.push({
        type: "image",
        image: `/images/img-1/blog/${lines[i + 1]}`,
        alt: post.value.title,
      });
      i++;
    } else if (line === "[QUOTE]") {
      result.push({ type: "quote", text: lines[i + 1] });
      i++;
    } else if (line === "[TABLE]") {
      const headers = lines[i + 1].split("|").map((item) => item.trim());
      const rows = [];

      i += 2;

      while (
        i < lines.length &&
        !lines[i].startsWith("# ") &&
        !lines[i].startsWith("## ") &&
        !lines[i].startsWith("[IMAGE]") &&
        !lines[i].startsWith("[QUOTE]") &&
        !lines[i].startsWith("[CTA]") &&
        !lines[i].startsWith("[SIDECTA]") &&
        !lines[i].startsWith("[TABLE]")
      ) {
        rows.push(lines[i].split("|").map((item) => item.trim()));
        i++;
      }

      i--;
      result.push({ type: "table", headers, rows });
    } else if (line === "[SIDECTA]") {
      i += 5;
    } else if (line === "[CTA]") {
      result.push({
        type: "cta",
        title: lines[i + 1],
        buttonText: lines[i + 2],
        link: lines[i + 3],
      });
      i += 3;
    } else {
      result.push({ type: "paragraph", text: line });
    }
  }

  return result;
});

const formattedDate = computed(() => {
  if (!post.value?.date) return "";

  const match = post.value.date.match(/\((.*?)\)/);
  if (!match) return post.value.date;

  const parts = match[1].split(",");
  return `${parts[0]}-${Number(parts[1]) + 1}-${parts[2]}`;
});
</script>

<style scoped>
.blog-detail-page {
  padding: 110px 20px 80px;
  background: #f7faff;
}

.blog-hero {
  max-width: 1120px;
  margin: 0 auto 70px;
  display: grid;
  grid-template-columns: 1fr 1.05fr;
  gap: 48px;
  align-items: center;
}

.hero-text {
  text-align: left;
}

.category {
  display: inline-block;
  color: #005eb8;
  font-weight: 700;
  margin-bottom: 18px;
}

h1 {
  font-size: 44px;
  line-height: 1.35;
  color: #0f2f57;
  margin: 0 0 24px;
}

.intro {
  font-size: 18px;
  line-height: 1.9;
  color: #5f6f82;
  margin-bottom: 18px;
}

.date {
  color: #8a98a8;
}

.hero-img {
  width: 100%;
  height: 430px;
  object-fit: cover;
  border-radius: 28px;
  box-shadow: 0 20px 50px rgba(0, 72, 155, 0.16);
}

.blog-layout {
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 48px;
  align-items: start;
}

.article {
  background: #ffffff;
  border-radius: 28px;
  padding: 48px;
  box-shadow: 0 16px 45px rgba(0, 72, 155, 0.08);
}

.article h2 {
  font-size: 28px;
  color: #0f2f57;
  margin: 56px 0 40px;
  text-align: center;
  position: relative;
}

.article h2::after {
  content: "";
  display: block;
  width: 80px;
  height: 4px;
  background: #005eb8;
  border-radius: 999px;
  margin: 16px auto 0;
}

.article p {
  color: #4f5f72;
  font-size: 17px;
  line-height: 2;
  margin-bottom: 36px;
  text-align: left;
}

.side-wrap {
  position: relative;
  min-height: 100%;
}

.side-card {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  box-sizing: border-box;
  transition: transform 0.08s linear;
  background: #ffffff;
  border-radius: 24px;
  padding: 28px;
  box-shadow: 0 16px 40px rgba(0, 72, 155, 0.1);
}

.side-card h3 {
  color: #0f2f57;
  margin-bottom: 12px;
}

.side-card p {
  font-weight: 700;
  color: #005eb8;
  margin-bottom: 8px;
}

.side-card span {
  display: block;
  color: #63758a;
  line-height: 1.7;
  margin-bottom: 24px;
  text-align: left;
}

.cta-btn {
  display: block;
  text-align: center;
  background: #005eb8;
  color: #ffffff;
  padding: 13px 20px;
  border-radius: 999px;
  text-decoration: none;
  font-weight: 700;
}

.back-link {
  display: inline-block;
  margin-top: 10px;
  color: #005eb8;
  text-decoration: none;
  font-weight: 700;
}

.table-wrap {
  overflow-x: auto;
  margin: 36px 0;
}

table {
  width: 100%;
  border-collapse: collapse;
  min-width: 680px;
  background: #ffffff;
  border-radius: 18px;
  overflow: hidden;
}

th {
  background: #005eb8;
  color: #ffffff;
  padding: 16px;
  text-align: left;
  white-space: nowrap;
}

td {
  padding: 16px;
  border-bottom: 1px solid #e4edf7;
  color: #4f5f72;
  line-height: 1.7;
}

.article-image img {
  width: 100%;
  /* border-radius: 22px; */
  box-shadow: 0 14px 35px rgba(0, 72, 155, 0.12);
}

blockquote {
  background: #eef6ff;
  border-left: 5px solid #005eb8;
  padding: 22px 26px;
  border-radius: 18px;
  color: #0f2f57;
  font-size: 19px;
  font-weight: 700;
  line-height: 1.8;
}

.article h5 {
  font-size: 21px;
  color: #0f2f57;
  margin: -20px 0 18px;
  line-height: 1.6;
  font-weight: 700;
}

@media (max-width: 900px) {
  .blog-hero,
  .blog-layout {
    grid-template-columns: 1fr;
  }

  h1 {
    font-size: 32px;
  }

  .hero-img {
    height: 260px;
  }

  .article {
    padding: 30px 24px;
  }

  .side-wrap {
    position: static;
  }

  .side-card {
    position: static;
    width: 100%;
    transform: none !important;
    box-sizing: border-box;
  }
}

@media (max-width: 768px) {
  .blog-detail-page {
    padding: 90px 16px 50px;
    overflow-x: hidden;
  }

  .article {
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
  }

  .content-block {
    max-width: 100%;
  }

  .article-image {
    margin: 24px 0;
    overflow: hidden;
  }

  .article-image img {
    width: 100%;
    max-width: 100%;
    height: auto;
    display: block;
    object-fit: contain;
  }

  .table-wrap {
    width: 100%;
    overflow-x: auto;
  }

  table {
    min-width: 0;
    width: 100%;
  }
}
</style>
